/**
 * B549 (7) — the organiser's HOSTED accept-&-pay page (447:2), drawn to
 * docs/design/skin-v2/pages/spec/redline-quote-page.json. Every element the
 * sidecar names that we draw carries its `data-redline` hook; geometry and
 * registers live in styles.css on the `qp-*` names.
 *
 * Named differences from the frame (recorded on the board line for the
 * redline measure):
 *  - (7c) the provider's card element mounts in the frame's card well once
 *    the acceptance has raised the invoice's payment — so ACCEPT & PAY is two
 *    presses (accept, then pay), not one; the EXPRESS wallet row is not drawn
 *    (the element is card-only, B519), and the provider's hosted page stays the
 *    fallback when the element cannot load;
 *  - the reference reads GROUP QUOTE until acceptance mints the booking
 *    reference (no quote number exists), then GROUP BOOKING <ref>;
 *  - the lede counts the party as "<n> guests (<a> + <c> chaperones)" — the
 *    record does not know whether the party are students.
 */
import type { ReactElement } from "react";
import { Banner, Button, DataTable, EmptyState, NewTabLink, SegmentedControl, Skeleton, StageChip } from "@transact/ui";
import { formatMoney } from "../lib/money.js";
import { dayMonthWords } from "../lib/when.js";
import {
  afterAcceptWords,
  ctaWords,
  deadWords,
  expiryWords,
  instalmentTitle,
  ledeWords,
  preparedForWords,
  quoteRefWords,
  termsWords,
  type QuotePageFace,
  type QuotePageInstalment,
  type QuotePayBy,
} from "../lib/quotePage.js";

export interface QuotePageProps {
  readonly page: QuotePageFace | undefined;
  readonly now: number;
  readonly payBy: QuotePayBy;
  readonly onPayBy: (payBy: QuotePayBy) => void;
  readonly onRespond: () => void;
  /** The refusal's way back (B213 (ii)): re-open the link and read its state as it now stands. */
  readonly onReopen?: () => void;
  readonly loading?: boolean;
  readonly busy?: boolean;
  readonly error?: { readonly message: string; readonly reference?: string | undefined } | undefined;
  /**
   * B549 (7c): the provider's card element in the frame's card well — "mounted"
   * (the controller mounts it into #qp-card-element), "confirming" (paid; the
   * provider's word is on its way), or why it could not load (+ the hosted page).
   */
  readonly card?: "mounted" | "confirming" | { readonly unavailable: string; readonly fallbackUrl?: string | undefined } | undefined;
}

const PAY_BY = [
  { value: "CARD", label: "CARD", redline: "quote-page-pay-by-card" },
  { value: "BANK_TRANSFER", label: "BANK TRANSFER", redline: "quote-page-pay-by-bank" },
] as const;

function chipOf(i: QuotePageInstalment, now: number, first: boolean): { tone: "due" | "info" | "paid"; words: string } {
  if (i.paidMinor >= i.amountMinor) return { tone: "paid", words: "PAID" };
  if (first && i.dueAt <= now) return { tone: "due", words: "DUE NOW" };
  return { tone: "info", words: "LATER" };
}

function Bar(props: { readonly seller: string; readonly refWords?: string }): ReactElement {
  return (
    <header className="qp-bar">
      <p className="qp-brand" data-redline="quote-page-brand">
        {props.seller.toUpperCase()}
      </p>
      {props.refWords === undefined ? null : (
        <p className="qp-ref" data-redline="quote-page-quote-ref">
          {props.refWords}
        </p>
      )}
    </header>
  );
}

export function QuotePage(props: QuotePageProps): ReactElement {
  const page = props.page;
  if (page === undefined) {
    return (
      <main className="qp qp--bare" aria-label="Group quote">
        {props.loading === true ? (
          <div className="qp-bare-body" role="status" aria-label="Opening your quote">
            <Skeleton lines={6} />
          </div>
        ) : (
          <div className="qp-bare-body">
            <EmptyState
              title="This link doesn't open a quote"
              body="Check that the whole link from the email reached this page — some mail apps cut long links. If it still won't open, reply to the email you got it in and ask for a fresh link."
            />
          </div>
        )}
      </main>
    );
  }

  const q = page.quote;
  if (q === undefined) {
    const dead = deadWords(page);
    return (
      <main className="qp" aria-label="Group quote">
        <Bar seller={page.seller} />
        <div className="qp-bare-body">
          <EmptyState title={dead.title} body={dead.body} />
        </div>
      </main>
    );
  }

  const tz = page.timeZone;
  const m = (minor: number): string => formatMoney({ amount: minor, currency: q.currency });
  const cta = ctaWords(page, props.payBy);
  const firstOwedIndex = q.schedule.findIndex((i) => !i.afterVisit && i.paidMinor < i.amountMinor);
  const choosing = q.payOnline && page.state !== "PAID" && q.payFirst !== undefined;

  const acceptCaption =
    page.state === "PAID" ? "BOOKING CONFIRMED" : page.state === "ACCEPTED" ? "ACCEPTED · PAY WHAT'S DUE" : q.payFirst?.kind === "DEPOSIT" ? "ACCEPT & PAY THE DEPOSIT" : "ACCEPT THIS QUOTE";

  return (
    <main className="qp" aria-label="Group quote">
      <Bar seller={page.seller} refWords={quoteRefWords(page)} />
      {page.state === "OPEN" ? (
        <div className="qp-expiry" data-redline="quote-page-expiry-strip">
          <p className="qp-expiry-text" data-redline="quote-page-expiry-text">
            {expiryWords(page)}
          </p>
        </div>
      ) : null}
      <div className="qp-grid">
        <section className="qp-main" aria-labelledby="qp-title">
          <p className="qp-prepared" data-redline="quote-page-prepared-for">
            {preparedForWords(q)}
          </p>
          <h1 className="qp-title" id="qp-title" data-redline="quote-page-title">
            {page.state === "OPEN" ? "Your group quote" : "Your group booking"}
          </h1>
          <p className="qp-lede" data-redline="quote-page-lede">
            {ledeWords(q, tz)}
          </p>

          <div className="qp-card qp-lines" data-redline="quote-page-card-lines">
            <DataTable
              columns={[
                { key: "item", header: "ITEM", headerRedline: "quote-page-col-item", priority: 1 },
                { key: "public", header: "PUBLIC", headerRedline: "quote-page-col-public", align: "amount", priority: 4, register: "muted" },
                { key: "yours", header: "YOUR PRICE", headerRedline: "quote-page-col-your-price", align: "amount", priority: 3 },
                { key: "qty", header: "QTY", headerRedline: "quote-page-col-qty", align: "num", priority: 5 },
                { key: "line", header: "LINE", headerRedline: "quote-page-col-line", align: "amount", priority: 2 },
              ]}
              rows={q.lines.map((l, n) => ({
                key: `${n}`,
                cells: {
                  item: (
                    <span className="qp-line-name" data-redline={`quote-page-line-${n + 1}`}>
                      {l.name}
                    </span>
                  ),
                  public: l.publicUnitMinor === undefined ? "" : m(l.publicUnitMinor),
                  yours: m(l.groupUnitMinor),
                  qty: `${l.qty}`,
                  line: m(l.lineMinor),
                },
              }))}
            />
            <div className="qp-total">
              <span className="qp-total-label" data-redline="quote-page-total-label">
                Total
              </span>
              <span className="qp-total-figure" data-redline="quote-page-total">
                {m(q.totalMinor)}
              </span>
            </div>
            {q.publicTotalMinor !== undefined && q.publicTotalMinor > q.totalMinor ? (
              <p className="qp-saving">You save {m(q.publicTotalMinor - q.totalMinor)} against the public price.</p>
            ) : null}
          </div>

          <div className="qp-card qp-schedule" data-redline="quote-page-card-schedule">
            <p className="qp-caption" data-redline="quote-page-schedule-caption">
              PAYMENT SCHEDULE
            </p>
            <ul className="qp-instalments">
              {q.schedule.map((i, n) => {
                const hook = i.kind === "BALANCE" ? "balance" : "deposit";
                const chip = chipOf(i, props.now, n === firstOwedIndex);
                return (
                  <li className="qp-instalment" key={`${i.kind}-${n}`}>
                    <span className="qp-instalment-title" data-redline={`quote-page-${hook}-title`}>
                      {instalmentTitle(i, q.totalMinor)}
                    </span>
                    <span className="qp-instalment-due">{i.dueAt <= props.now && n === firstOwedIndex ? "on acceptance" : `by ${dayMonthWords(i.dueAt, { locale: "en-US", timeZone: tz })}`}</span>
                    <span className="qp-instalment-amount" data-redline={`quote-page-${hook}-amount`}>
                      {m(i.amountMinor)}
                    </span>
                    <StageChip tone={chip.tone} redline={`quote-page-${hook}-chip`} textRedline={`quote-page-${hook}-chip-text`}>
                      {chip.words}
                    </StageChip>
                  </li>
                );
              })}
            </ul>
          </div>
          <p className="qp-terms" data-redline="quote-page-terms">
            {termsWords(q)}
          </p>
          {/* B615 leg 3d — the show's rules before the organiser accepts; no facts, no card. */}
          {q.houseFacts !== undefined && q.houseFacts.length > 0 ? (
            <div className="qp-card qp-facts" data-redline="quote-page-card-facts">
              <p className="qp-caption">BEFORE YOU GO</p>
              <ul className="qp-facts-list">
                {q.houseFacts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <aside className="qp-card qp-accept" data-redline="quote-page-card-accept" aria-labelledby="qp-accept-caption">
          <p className="qp-caption" id="qp-accept-caption" data-redline="quote-page-accept-caption">
            {acceptCaption}
          </p>
          {page.state === "PAID" ? (
            <p className="qp-done" role="status">
              Paid and confirmed{q.bookingRef !== undefined ? ` — booking ${q.bookingRef}` : ""}. {page.seller} sends your tickets before the visit.
            </p>
          ) : (
            <>
              {page.state === "ACCEPTED" ? (
                <p className="qp-done" role="status">
                  Accepted{q.bookingRef !== undefined ? ` — booking ${q.bookingRef}` : ""}. Your seats are held while the payment lands.
                </p>
              ) : null}
              {q.payFirst !== undefined ? (
                <>
                  <p className="qp-label" data-redline="quote-page-due-today-label">
                    {q.payFirst.dueNow ? "DUE TODAY" : `DUE BY ${dayMonthWords(q.payFirst.dueAt, { locale: "en-US", timeZone: tz }).toUpperCase()}`}
                  </p>
                  <p className="qp-due" data-redline="quote-page-due-today">
                    {m(q.payFirst.amountMinor)}
                  </p>
                </>
              ) : null}
              {choosing ? (
                <>
                  <p className="qp-label" data-redline="quote-page-pay-by-label">
                    PAY BY
                  </p>
                  <SegmentedControl
                    legend="Pay by"
                    name="qp-pay-by"
                    options={PAY_BY}
                    value={props.payBy}
                    onChange={(v) => props.onPayBy(v as QuotePayBy)}
                    redline="quote-page-pay-by"
                    activeRedline="quote-page-pay-by-active"
                  />
                </>
              ) : null}
              {props.card === "mounted" ? <div className="qp-card-well" id="qp-card-element" data-redline="quote-page-card-element" /> : null}
              {typeof props.card === "object" ? (
                <p className="qp-how" role="status">
                  The card form could not open here ({props.card.unavailable}).{" "}
                  {props.card.fallbackUrl !== undefined ? <NewTabLink href={props.card.fallbackUrl}>Pay on the provider&apos;s secure page</NewTabLink> : null}
                </p>
              ) : null}
              {props.card === "confirming" ? (
                <p className="qp-done" role="status">
                  Payment received — {page.seller} confirms your booking as soon as the provider says it has settled.
                </p>
              ) : null}
              <p className="qp-how" id="qp-how">
                {afterAcceptWords(page, props.payBy, props.card === "mounted")}
              </p>
              {props.error !== undefined ? (
                <Banner tone="danger" id="qp-refusal" reference={props.error.reference}>
                  {props.error.message}{" "}
                  <Button variant="textlink" onClick={props.onReopen ?? (() => undefined)}>
                    Open the quote again
                  </Button>
                </Banner>
              ) : null}
              {cta !== undefined && props.card !== "confirming" ? (
                <Button variant="primary" size="lg" busy={props.busy === true} onClick={props.onRespond} describedBy="qp-how" data={{ redline: "quote-page-cta" }}>
                  <span data-redline="quote-page-cta-text">{cta}</span>
                </Button>
              ) : null}
              {page.state === "OPEN" ? (
                <p className="qp-fine" data-redline="quote-page-fine-print">
                  By accepting you agree to the terms on the left. Your seats stay held until {dayMonthWords(page.expiresAt, { locale: "en-US", timeZone: tz })}; after that {page.seller} may release them.
                </p>
              ) : null}
            </>
          )}
        </aside>
      </div>
    </main>
  );
}
